import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import ConsentModal from "./ConsentModal";
import { isAcceptanceRequired } from "@/lib/api";

/**
 * Wraps any call the server may refuse pending acceptance (HTTP 428).
 *
 * The gate is driven by the server's refusal rather than by the client
 * pre-checking whether consent is needed. That ordering matters: the backend is
 * the thing that decides, so the UI cannot drift out of step with it, and a
 * version bump starts gating immediately without a frontend deploy.
 *
 * Usage:
 *   const { guard, gateNode } = useAcceptanceGate();
 *   await guard("checkout", () => createCheckout(...));
 *   ...
 *   {gateNode}
 */
export function useAcceptanceGate() {
  const [pending, setPending] = useState(null);

  const guard = useCallback(async (context, fn, { subjectRef = null, title, lede } = {}) => {
    try {
      return await fn();
    } catch (e) {
      if (isAcceptanceRequired(e)) {
        setPending({ context, retry: fn, subjectRef, title, lede });
        return undefined;
      }
      throw e;
    }
  }, []);

  const handleAccepted = useCallback(async () => {
    const p = pending;
    setPending(null);
    if (p?.retry) {
      try {
        await p.retry();
      } catch (e) {
        // This used to swallow the error on the theory that the caller's
        // handler would surface it. It does not: by this point we are past the
        // caller's try/catch, so a failed retry produced a silent no-op - the
        // consent modal closed and nothing happened, with nothing logged and
        // nothing shown. Say what broke instead.
        console.error("Consent retry failed", e);
        const detail = e?.response?.data?.detail;
        toast.error(
          (typeof detail === "string" && detail)
          || e?.message
          || "That went through, but the action after it did not. Please try again.",
          { duration: 8000 },
        );
      }
    }
  }, [pending]);

  const gateNode = pending ? (
    <ConsentModal
      context={pending.context}
      subjectRef={pending.subjectRef}
      title={pending.title}
      lede={pending.lede}
      onAccepted={handleAccepted}
    />
  ) : null;

  return { guard, gateNode, gateOpen: Boolean(pending) };
}

export default useAcceptanceGate;
